import { canEditMandatoryFields } from '@/shared/rbac'
import type { Tour } from '../../types'
import { target } from './target'

/** Dataset pages built on KbBrowserPage (the only ones with the search box + table anchors). */
const KB_CODE_BROWSER = /^\/kb\/(cpt|hcpcs|modifiers|pcs)\/?$/

export const libraryTours: Tour[] = [
  {
    id: 'page:kb',
    version: 1,
    title: 'Knowledge Base',
    kind: 'page',
    match: /^\/kb\/?$/,
    // router: kb/* has no route guard; offered on the sidebar's rule (knowledge_base read).
    access: { when: (a) => a.can('knowledge_base', 'read') },
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Knowledge Base', body: 'Reference data for medical coding: code systems, coverage, mappings and payers.' },
      { id: 'groups', target: target('kb.groups'), side: 'bottom', title: 'Dataset groups', body: 'Switch between groups of datasets.' },
      { id: 'search', target: target('kb.search'), side: 'bottom', title: 'Find a dataset', body: 'Type to filter the cards by name.' },
      { id: 'cards', target: target('kb.cards'), side: 'top', title: 'Open a dataset', body: 'Each card opens a searchable table, such as CPT, ICD-10-CM, NCCI or Guidelines.' },
    ],
  },
  {
    id: 'page:kb-dataset',
    version: 1,
    title: 'Knowledge Base dataset',
    kind: 'page',
    // Dataset pages only — not the import history or audit log.
    match: /^\/kb\/(?!imports\/?$|audit\/?$)[^/]+\/?$/,
    access: { when: (a) => a.can('knowledge_base', 'read') },
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'This dataset', body: 'One reference dataset. It always shows the latest loaded data.' },
      // Only the code-browser pages (CPT, HCPCS, Modifiers, PCS) carry these two.
      { id: 'search', target: target('kb.dataset-search'), onlyOn: KB_CODE_BROWSER, waitMs: 2500, side: 'bottom', title: 'Search this dataset', body: 'Search by code or description.' },
      { id: 'table', target: target('kb.dataset-table'), onlyOn: KB_CODE_BROWSER, waitMs: 2500, side: 'top', title: 'Details', body: 'Click a row to open its full details.' },
    ],
  },
  {
    id: 'page:mandatory-fields',
    version: 2,
    title: 'Mandatory Fields',
    kind: 'page',
    match: /^\/platform-config(\/mandatory-fields)?\/?$/,
    access: { when: (a) => a.can('mandatory_fields', 'read') }, // router: platform-config/*
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Mandatory Fields', body: 'Chart fields the engine needs before it codes a chart. A chart missing one goes to the Exceptions tab of the queue.' },
      // Edit / deactivate need the server's write gate (canEditMandatoryFields).
      { id: 'table', target: target('mandatory.table'), access: { when: canEditMandatoryFields }, side: 'top', title: 'Where a rule applies', body: 'Rules can apply everywhere, to a specialty, or to one client. Use the row actions to edit a field; deactivating one stops it being required.' },
      { id: 'table-read', target: target('mandatory.table'), access: { when: (a) => !canEditMandatoryFields(a) }, side: 'top', title: 'Where a rule applies', body: 'Rules can apply everywhere, to a specialty, or to one client. Each row shows where its field is required.' },
    ],
  },
  {
    id: 'page:prompt-library',
    version: 1,
    title: 'Prompt Library',
    kind: 'page',
    match: /^\/prompt-config(\/workflows)?\/?$/,
    access: { gate: 'canConfigurePrompts' }, // router: prompt-config/* (edit, stricter than the sidebar)
    mayHoldEdits: true, // inline WorkflowForm (name, description, textarea)
    steps: [
      { id: 'title', target: target('page-header'), side: 'bottom', title: 'Prompt Workflows', body: 'The prompts the coding engine uses, organized by workflow.' },
      { id: 'tabs', target: target('prompts.tabs'), side: 'bottom', title: 'Building blocks', body: 'Parameters and sections are the reusable parts workflows are built from.' },
      { id: 'search', target: target('prompts.search'), whenMissing: 'Once workflows exist, a search box here finds one by name.',  waitMs: 2500, side: 'bottom', title: 'Find a workflow', body: 'Search by name, or filter by status.' },
    ],
  },
  {
    id: 'page:configurations',
    version: 1,
    title: 'Configurations',
    kind: 'page',
    match: /^\/client-config(\/.*)?$/,
    access: { gate: 'canConfigureClient' }, // router: client-config/* (read:all, stricter than the sidebar)
    mayHoldEdits: true, // module / client-config editors save explicitly
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Configurations', body: 'Which pipeline modules run, and how each client is set up.' },
      { id: 'sections', target: target('config.sections'), side: 'bottom', title: 'Sections', body: '"Prediction" controls the engine\'s modules and client configuration; "Integration" covers how chart fields arrive from each EMR; "System Config" holds per-client system settings; and "Model Pricing" the AI model rate catalog.' },
      { id: 'prediction-tabs', target: target('config.prediction-tabs'), onlyOn: /^\/client-config(\/prediction(\/.*)?)?\/?$/, waitMs: 1500, side: 'bottom', title: 'Prediction settings', body: '"Modules", "Submodules" and "Client Configuration" by facility, specialty and report type.' },
    ],
  },
]
